import React, { useState } from "react";
import { Card, Row, Col, Spinner, Alert, Form, InputGroup, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faWallet,
    faSearch,
    faTimes,
    faMoneyBillWave,
    faInfoCircle,
    faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useBreakpoints } from "providers/BreakpointsProvider";
import Button from "components/base/Button";
import { useContas } from "hooks/useContas";
import { ContaType } from "services/contaService";
import { contasBreadcrumbItems } from "data/conta";
import { useNavigate } from "react-router-dom";

// Componentes extraídos
import BotaoCriarConta from "./components/BotaoCriarConta";

const ContasPage: React.FC = () => {
    // Estado para busca
    const [termoBusca, setTermoBusca] = useState('');

    // Hooks
    const contasQuery = useContas();
    const { breakpoints } = useBreakpoints();
    const navigate = useNavigate();

    // Verificações responsivas
    const isMobile = breakpoints.down('md');

    // Filtragem de contas
    const contasFiltradas = React.useMemo(() => {
        if (!contasQuery.data || !termoBusca.trim()) {
            return contasQuery.data || [];
        }

        const termo = termoBusca.toLowerCase();
        return contasQuery.data.filter(conta =>
            conta.nome.toLowerCase().includes(termo)
        );
    }, [contasQuery.data, termoBusca]);

    // Formatação de valores
    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    // Função para navegar para detalhes da conta
    const handleContaClick = (conta: ContaType) => {
        navigate(`/financeiro/contas/${conta.id}`);
    };

    // Render de card de conta para mobile
    const renderContaCard = (conta: ContaType) => (
        <Card
            key={conta.id}
            className="mb-3 border border-opacity-25 shadow-sm"
            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
            onClick={() => handleContaClick(conta)}
        >
            <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1 me-2">
                        <h6 className="mb-1 text-primary-emphasis d-flex align-items-center">
                            <FontAwesomeIcon icon={faWallet} className="me-2" />
                            {conta.nome}
                        </h6>
                        <small className="text-body-secondary">
                            Criada em: {new Date(conta.createdAt).toLocaleDateString('pt-BR')}
                        </small>
                    </div>
                    <div className="text-end">
                        <div className={`h5 mb-1 ${conta.saldo >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}`}>
                            {formatarMoeda(conta.saldo)}
                        </div>
                        <Badge bg={conta.saldo >= 0 ? 'success' : 'danger'} className="small">
                            {conta.saldo >= 0 ? 'Positivo' : 'Negativo'}
                        </Badge>
                    </div>
                </div>

                <div className="d-flex justify-content-end align-items-center mt-2">
                    <small className="text-body-secondary me-2">Ver detalhes</small>
                    <FontAwesomeIcon icon={faChevronRight} className="text-body-secondary" />
                </div>
            </Card.Body>
        </Card>
    );

    // Render da tabela para desktop
    const renderContaTable = () => (
        <div className="table-responsive">
            <table className="table table-hover">
                <thead>
                    <tr>
                        <th>Conta</th>
                        <th>Saldo</th>
                        <th>Status</th>
                        <th>Criada em</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {contasFiltradas.map((conta) => (
                        <tr
                            key={conta.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleContaClick(conta)}
                            className="conta-row"
                        >
                            <td>
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faWallet} className="me-2 text-primary-emphasis" />
                                    <strong>{conta.nome}</strong>
                                </div>
                            </td>
                            <td>
                                <span className={`fw-bold ${conta.saldo >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}`}>
                                    {formatarMoeda(conta.saldo)}
                                </span>
                            </td>
                            <td>
                                <Badge bg={conta.saldo >= 0 ? 'success' : 'danger'}>
                                    {conta.saldo >= 0 ? 'Positivo' : 'Negativo'}
                                </Badge>
                            </td>
                            <td>
                                <small>{new Date(conta.createdAt).toLocaleDateString('pt-BR')}</small>
                            </td>
                            <td className="text-end">
                                <FontAwesomeIcon icon={faChevronRight} className="text-body-secondary" />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    if (contasQuery.isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </div>
        );
    }

    if (contasQuery.isError) {
        return (
            <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                <div className="d-flex align-items-center text-danger-emphasis">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    Erro ao carregar contas. Tente novamente mais tarde.
                </div>
            </Alert>
        );
    }

    const totalContas = contasQuery.data?.length || 0;
    const saldoTotal = contasQuery.data?.reduce((total, conta) => total + conta.saldo, 0) || 0;

    return (
        <div>
            <PageBreadcrumb items={contasBreadcrumbItems} />

            {/* Header Responsivo */}
            <div className={`d-flex ${isMobile ? 'flex-column gap-3' : 'justify-content-between align-items-center'} mb-4`}>
                <div className={isMobile ? 'text-center' : ''}>
                    <h2 className="mb-2 text-body-emphasis">
                        Gestão de Contas
                    </h2>
                    <p className="text-body-secondary mb-0">
                        Gerencie suas contas financeiras e movimentações
                    </p>
                </div>

                <div className={`d-flex ${isMobile ? 'flex-column gap-2 align-items-center' : 'align-items-center gap-3'}`}>
                    <div className="text-center">
                        <div className="small text-body-secondary">Total em Contas</div>
                        <div className={`fw-bold ${saldoTotal >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}`}>
                            {formatarMoeda(saldoTotal)}
                        </div>
                    </div>

                    <BotaoCriarConta>
                        {isMobile ? 'Nova Conta' : 'Criar Conta'}
                    </BotaoCriarConta>
                </div>
            </div>

            {/* Contas */}
            <Card className="border border-opacity-25">
                <Card.Header className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'}`}>
                    <h5 className="mb-0 text-body-emphasis">
                        <FontAwesomeIcon icon={faWallet} className="me-2" />
                        Contas ({totalContas})
                    </h5>
                    {totalContas > 0 && (
                        <small className="text-body-secondary">
                            Clique em uma conta para ver detalhes e extrato
                        </small>
                    )}
                </Card.Header>
                <Card.Body className={isMobile ? 'p-2' : ''}>
                    {totalContas === 0 ? (
                        <div className="text-center py-4">
                            <FontAwesomeIcon icon={faMoneyBillWave} size="3x" className="text-body-secondary mb-3" />
                            <h5 className="text-body-secondary mb-3">Nenhuma conta cadastrada</h5>
                            <p className="text-body-secondary mb-4">
                                Crie sua primeira conta para começar a gerenciar suas finanças.
                            </p>
                            <div className="d-flex justify-content-center">
                                <BotaoCriarConta>
                                    Criar Primeira Conta
                                </BotaoCriarConta>
                            </div>
                        </div>
                    ) : (
                        <div>
                            {/* Busca */}
                            <div className="mb-3">
                                <Row className="align-items-center g-2">
                                    <Col>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FontAwesomeIcon icon={faSearch} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder={isMobile ? "Buscar conta..." : "Buscar por nome da conta..."}
                                                value={termoBusca}
                                                onChange={(e) => setTermoBusca(e.target.value)}
                                            />
                                            {termoBusca && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setTermoBusca('')}
                                                    title="Limpar busca"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Col>
                                    {!isMobile && (
                                        <Col xs="auto">
                                            <small className="text-body-secondary">
                                                {termoBusca ? (
                                                    `${contasFiltradas.length} de ${totalContas} contas`
                                                ) : (
                                                    `${totalContas} contas`
                                                )}
                                            </small>
                                        </Col>
                                    )}
                                </Row>
                                {isMobile && (
                                    <div className="text-center mt-2">
                                        <small className="text-body-secondary">
                                            {termoBusca ? (
                                                `${contasFiltradas.length} de ${totalContas} contas encontradas`
                                            ) : (
                                                `${totalContas} contas cadastradas`
                                            )}
                                        </small>
                                    </div>
                                )}
                            </div>

                            {contasFiltradas.length === 0 && termoBusca ? (
                                <div className="text-center py-4">
                                    <FontAwesomeIcon icon={faSearch} size="3x" className="text-body-secondary mb-3" />
                                    <h5 className="text-body-secondary mb-3">Nenhuma conta encontrada</h5>
                                    <p className="text-body-secondary mb-4">
                                        Não foram encontradas contas que correspondam à busca "{termoBusca}".
                                    </p>
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setTermoBusca('')}
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                                        Limpar Busca
                                    </Button>
                                </div>
                            ) : (
                                <div>
                                    {isMobile ? (
                                        // Layout em cards para mobile
                                        <div className={isMobile ? 'px-2' : ''}>
                                            {contasFiltradas.map(conta => renderContaCard(conta))}
                                        </div>
                                    ) : (
                                        // Layout em tabela para desktop
                                        renderContaTable()
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>


        </div>
    );
};

export default ContasPage; 